import type { ReasonAction, ReasonCode } from './reasons';

/**
 * The message catalogue. `customer_message` values arrive as keys with parameters (§13.12);
 * screens render them here. Plain words, the glossary's words, never a code or a vendor name.
 * `{name}` placeholders are filled from `params`; a missing parameter renders as an em dash.
 */
export type MessageKey = `reason.${ReasonCode}` | `action.${ReasonAction}`;

export type MessageParams = Record<string, string | number>;

const reasonMessages: Record<`reason.${ReasonCode}`, string> = {
  'reason.source_file_unreadable': 'The file {file} could not be opened',
  'reason.unsupported_format': '{file} is a {format} file, which Paige does not read',
  'reason.page_count_mismatch': '{file} says {expected} pages but {actual} arrived',
  'reason.render_failed': 'Page {page} could not be rendered',
  'reason.ocr_failed': 'Page {page} could not be read',
  'reason.reader_refused': 'Cortexa could not make sense of page {page}',
  'reason.reader_unavailable': 'Cortexa is catching up; reading resumes by itself',
  'reason.pool_quota_exhausted': 'Cortexa is at capacity; reading resumes by itself',
  'reason.lease_expired_repeatedly': 'This document keeps timing out at the {stage} stage',
  'reason.ambiguous_unresolved':
    'Two documents may be one: no boundary found between pages {from} and {to}',
  'reason.destination_auth_failed': '{destination} no longer accepts the saved credentials',
  'reason.destination_mapping_invalid': '{destination} rejected the field {field}',
  'reason.destination_unreachable': '{destination} has not answered since {since}',
  'reason.destination_ambiguous': 'More than one destination matches {type} documents',
  'reason.tenant_over_quota': 'This company has used its pages for {period}',
  'reason.credits_exhausted': 'Pages ran out; {waiting} documents are waiting',
  'reason.tenant_paused': 'Intake is paused for this company',
  'reason.sender_quarantined': 'Email from {sender} is held until someone releases it',
  'reason.engine_stalled': 'The job has stopped moving; Paige is restarting it',
  'reason.engine_unrecoverable': 'The job could not be restarted; Ademero support is looking',
  'reason.engine_workspace_mismatch':
    'The job workspace does not match its record; Ademero support is looking',
  'reason.workspace_migration_failed': 'The job is being updated; documents resume shortly',
  'reason.workspace_restore_failed': 'The job is being restored; documents resume shortly',
  'reason.disk_attach_failed': 'Storage for this job is being reattached',
  'reason.disk_full': 'Storage for this job is full; Ademero is adding more',
  'reason.projection_lagging': 'The overview is catching up with recent work',
  'reason.reader_generation_retired':
    'This document was read by a retired Cortexa release and will be reread',
  'reason.learning_gate_refused': 'The {field} gate is at {value} of {target}; still watching',
  'reason.answer_truncated':
    'Cortexa ran out of room answering for this document; it asks again by itself, then Ademero support looks',
  'reason.stage_timeout': 'This step took too long and was stopped; Paige tries it again by itself',
  'reason.source_file_set_aside':
    'The file {file} is too large or too complex for Paige to open safely, so it was set aside. The rest of the batch was read. Split it into smaller files and send them again.',
};

const actionMessages: Record<`action.${ReasonAction}`, string> = {
  'action.review': 'Review the document',
  'action.confirm_split': 'Confirm the split',
  'action.choose_type': 'Choose the type',
  'action.correct_field': 'Correct {field}',
  'action.replace_file': 'Replace the file',
  'action.retry': 'Retry now',
  'action.reconnect_destination': 'Reconnect {destination}',
  'action.fix_mapping': 'Fix the mapping',
  'action.choose_destination': 'Choose the destination',
  'action.buy_credits': 'Add pages',
  'action.resume': 'Resume intake',
  'action.release_sender': 'Release the email',
  'action.contact_support': 'Contact Ademero support',
  'action.wait': 'Nothing to do; Paige retries by itself',
  'action.skip': 'Skip this document',
};

export const messages: Record<MessageKey, string> = { ...reasonMessages, ...actionMessages };

export function formatMessage(key: MessageKey, params: MessageParams = {}): string {
  const template = messages[key];
  return template.replace(/\{(\w+)\}/g, (_, name: string) => {
    const v = params[name];
    return v === undefined ? '—' : String(v);
  });
}
